import type { AgentAuthor, Note, Site } from "./api.ts";
import { agentGlyph, type Person } from "./lib/people.ts";

/** The path of a note's page, without origin, query or fragment: "/pricing". */
export function pagePath(pageUrl: string): string {
  try {
    return new URL(pageUrl).pathname || "/";
  } catch {
    return pageUrl;
  }
}

/** Who wrote a note or reply, as the dashboard names and draws them. */
export interface AuthorView {
  readonly id: string;
  readonly name: string;
  readonly kind: "member" | "agent" | "guest";
  readonly agent: boolean;
  /** For agents: "API key "Design QA agent"" or "MCP"; null for people. */
  readonly via: string | null;
  /** The disc: member colour from the author id, agent glyph, neutral guest. */
  readonly person: Person;
}

/**
 * The people rule. A missing `author_kind` means agent when `agent` is
 * present, otherwise member, as in the SDK.
 */
export function authorView(input: {
  readonly author: string;
  readonly author_kind?: "member" | "agent" | "guest" | undefined;
  readonly author_name?: string | undefined;
  readonly agent?: AgentAuthor | undefined;
}): AuthorView {
  const kind =
    input.agent !== undefined ? "agent" : (input.author_kind ?? "member");
  if (kind === "guest") {
    const name = input.author_name ?? "Guest";
    return {
      id: input.author,
      name,
      kind,
      agent: false,
      via: null,
      person: { kind, name },
    };
  }
  if (kind === "member") {
    const name = input.author_name ?? "Reviewer";
    return {
      id: input.author,
      name,
      kind,
      agent: false,
      via: null,
      person: { kind, id: input.author, name },
    };
  }
  const name = input.agent?.name ?? input.author_name ?? "Agent";
  const via =
    input.agent?.kind === "mcp_oauth"
      ? "MCP"
      : input.author_name !== undefined && input.author_name !== name
        ? `API key "${input.author_name}"`
        : "API key";
  return {
    id: input.author,
    name,
    kind,
    agent: true,
    via,
    person: { kind, name, glyph: agentGlyph(name) },
  };
}

/** One page filter choice: a path on one Site. */
export interface PageFilter {
  readonly siteId: string;
  readonly path: string;
}

export interface PageGroup {
  readonly siteId: string;
  readonly siteName: string;
  readonly paths: readonly { readonly path: string; readonly count: number }[];
}

/** Group the loaded notes' page paths by Site, most notes first. */
export function pageGroups(notes: readonly Note[]): readonly PageGroup[] {
  const groups = new Map<
    string,
    { siteName: string; counts: Map<string, number> }
  >();
  for (const note of notes) {
    const group = groups.get(note.site_id) ?? {
      siteName: note.site_name ?? "Site",
      counts: new Map<string, number>(),
    };
    const path = pagePath(note.page_url);
    group.counts.set(path, (group.counts.get(path) ?? 0) + 1);
    groups.set(note.site_id, group);
  }
  return [...groups.entries()]
    .map(([siteId, group]) => ({
      siteId,
      siteName: group.siteName,
      paths: [...group.counts.entries()]
        .map(([path, count]) => ({ path, count }))
        .sort(
          (left, right) =>
            right.count - left.count || left.path.localeCompare(right.path),
        ),
    }))
    .sort((left, right) => left.siteName.localeCompare(right.siteName));
}

export function matchesPage(note: Note, filter: PageFilter | null): boolean {
  return (
    filter === null ||
    (note.site_id === filter.siteId && pagePath(note.page_url) === filter.path)
  );
}

/** Open, resolved and all counts for the state control, from the Site list. */
export function stateCounts(
  sites: readonly Site[],
  siteId: string | null,
): { readonly open: number; readonly resolved: number; readonly all: number } {
  let open = 0;
  let all = 0;
  for (const site of sites) {
    if (siteId !== null && site.id !== siteId) continue;
    open += site.open_note_count;
    all += site.note_count;
  }
  return { open, resolved: Math.max(0, all - open), all };
}

/** The sidebar count: open notes across the team's Sites. */
export function openNoteTotal(sites: readonly Site[]): number {
  return sites.reduce((total, site) => total + site.open_note_count, 0);
}

/** "1440 × 900" from the stored image metadata, or null when there is none. */
export function screenshotViewport(screenshot: unknown): string | null {
  if (typeof screenshot !== "object" || screenshot === null) return null;
  const width =
    "viewport_width" in screenshot ? screenshot.viewport_width : undefined;
  const height =
    "viewport_height" in screenshot ? screenshot.viewport_height : undefined;
  return typeof width === "number" &&
    typeof height === "number" &&
    width > 0 &&
    height > 0
    ? `${width} × ${height}`
    : null;
}
